import { useState } from "react";
import { MapPin, Phone, Mail, Inbox, Users, GraduationCap, Pencil, KeyRound, UserX, UserCheck, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Form, Input, Modal, Button } from "../../components/ui";
import { useSelector } from "react-redux";
import swal from "../../utils/swal";
import { PARENTS, UPLOADS_URL } from "../../config/constants";
import { Post } from "../../config/api/post";
import { generateParentUpdatePassword } from "../../config/helpers";
import { useUpdateParentStatus } from "../../query/parentQuery";
import { DetailCoverHero } from "../../components/DetailCoverHero";
import "../teachers/teacherDetails.css";

const CHILD_TONES = ["is-navy", "is-green", "is-yellow", "is-blue"];

function initials(firstName = "", lastName = "") {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "P";
}

function childAge(child) {
  if (child?.age) return `${child.age} yrs`;
  if (!child?.birthday) return "";
  const years = dayjs().diff(dayjs(child.birthday), "year");
  return Number.isFinite(years) && years >= 0 ? `${years} yrs` : "";
}

export default function ParentProfile({ parent, onDelete }) {
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const [resetOpen, setResetOpen] = useState(false);
  const [form] = Form.useForm();
  const { mutate: updateParentStatus, isPending: isUpdatingStatus } = useUpdateParentStatus();

  const fatherName = `${parent?.fatherFirstName || ""} ${parent?.fatherLastName || ""}`.trim() || "Parent";
  const motherName = `${parent?.motherFirstName || ""} ${parent?.motherLastName || ""}`.trim();
  const locationLabel = [parent?.city, parent?.state].filter(Boolean).join(", ");
  const addressParts = [parent?.address, parent?.city, parent?.state, parent?.zip].filter(Boolean);
  const children = parent?.childrens || [];
  const isActive = parent?.status === "ACTIVE";
  const statusLabel = parent?.status === "ACTIVE"
    ? "Active"
    : parent?.status === "PENDING"
      ? "Pending"
      : "Inactive";
  const role = children.length
    ? `Parent of ${children.length} ${children.length === 1 ? "child" : "children"}`
    : "Parent";

  const handleStatus = () => {
    const nextStatus = isActive ? "INACTIVE" : "ACTIVE";
    swal({
      title: "Are you sure?",
      text: `Do you want to ${nextStatus === "ACTIVE" ? "activate" : "deactivate"} this account?`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      updateParentStatus(
        { parentId: parent._id, status: nextStatus },
        {
          onSuccess: () => swal("Success", "Status updated successfully", "success"),
          onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
        }
      );
    });
  };

  const handleUpdatePassword = (values) => {
    if (values.newPassword && values.newPassword !== values.confirmPassword) {
      swal("Oops!", "Passwords do not match", "error");
      return;
    }

    swal({
      title: "Are you sure?",
      text: "Do you want to update the password?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      Post(
        PARENTS.resetParentPassword + parent._id,
        { password: values.newPassword || undefined },
        token
      )
        .then((response) => {
          if (response?.data?.status) {
            const generated = response?.data?.data?.password;
            const lines = ["Password updated successfully"];
            if (generated) lines.push(`Password: ${generated}`);
            swal("Success!", lines.join("\n"), "success");
            setResetOpen(false);
            form.resetFields();
          } else {
            swal("Oops!", response?.data?.message || response?.response?.data?.message || "Failed to update password", "error");
          }
        })
        .catch(() => {
          form.resetFields();
        });
    });
  };

  return (
    <div className="td-page">
      <div className="td-main">
      <article className="td-card">
        <DetailCoverHero
          coverKey={parent?._id}
          backLabel="Back to parents"
          onBack={() => navigate("/parents")}
        />

        <div className="td-body">
          <div className="td-identity">
            {parent?.image ? (
              <img
                className="td-avatar"
                src={`${UPLOADS_URL}/${parent.image}`}
                alt={fatherName}
              />
            ) : (
              <span className="td-avatar">{initials(parent?.fatherFirstName, parent?.fatherLastName)}</span>
            )}
            <div className="td-identity-row">
              <h2>{fatherName}</h2>
              <span className={`td-status is-${(parent?.status || "inactive").toLowerCase()}`}>
                {statusLabel}
              </span>
            </div>
            <p>{parent?.parentId ? `${role} · ${parent.parentId}` : role}</p>
          </div>

          <div className="td-chips">
            <span>
              <i className="is-orange">
                <MapPin size={14} />
              </i>
              {locationLabel || "Location not set"}
            </span>
            <span>
              <i className="is-orange">
                <Phone size={14} />
              </i>
              {parent?.phone || "Phone not set"}
            </span>
            <span>
              <i className="is-orange">
                <Mail size={14} />
              </i>
              {parent?.email || "Email not set"}
            </span>
          </div>

          <section className="td-section">
            <h3>Mother:</h3>
            {motherName ? (
              <p>{motherName}</p>
            ) : (
              <div className="td-empty">
                <Inbox size={22} />
                <span>No mother details yet</span>
              </div>
            )}
          </section>

          <section className="td-section">
            <h3>Address:</h3>
            {addressParts.length ? (
              <p>{addressParts.join(", ")}</p>
            ) : (
              <div className="td-empty">
                <Inbox size={22} />
                <span>No address listed yet</span>
              </div>
            )}
          </section>

          <section className="td-section">
            <h3>Family:</h3>
            {children.length ? (
              <ul>
                {children.map((child) => (
                  <li key={child._id}>
                    <strong>
                      {`${child.firstName || ""} ${child.lastName || ""}`.trim() || "Child"}
                      {child.classroom?.classroomName ? `, ${child.classroom.classroomName}` : ""}
                    </strong>
                    <em>{[child.rollNumber, childAge(child)].filter(Boolean).join(" · ") || "Student"}</em>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="td-empty">
                <Inbox size={22} />
                <span>No children assigned yet</span>
              </div>
            )}
          </section>
        </div>
      </article>

      <div className="td-actions">
        <button
          type="button"
          className="td-action is-primary"
          onClick={() => navigate(`/parents/edit/${parent._id}`)}
        >
          <Pencil size={15} />
          Edit details
        </button>
        <button
          type="button"
          className="td-action"
          onClick={() => setResetOpen(true)}
        >
          <KeyRound size={15} />
          Update password
        </button>
        <button
          type="button"
          className="td-action"
          disabled={isUpdatingStatus}
          onClick={handleStatus}
        >
          {isActive ? <UserX size={15} /> : <UserCheck size={15} />}
          {isActive ? "Deactivate" : "Activate"}
        </button>
        <button type="button" className="td-action is-danger" onClick={onDelete}>
          <Trash2 size={15} />
          Delete
        </button>
      </div>
      </div>

      <aside className="td-schedule">
        <div className="td-schedule-head">
          <h3>Children</h3>
          <p>
            {children.length
              ? `${children.length} ${children.length === 1 ? "child" : "children"} linked`
              : "No children assigned"}
          </p>
        </div>

        {children.length ? (
          <div className="td-schedule-list parent-children-list">
            {children.map((child, index) => {
              const childName = `${child.firstName || ""} ${child.lastName || ""}`.trim() || "Child";
              const className = child.classroom?.classroomName;
              return (
                <article
                  key={child._id}
                  className={`td-lesson ${CHILD_TONES[index % CHILD_TONES.length]}`}
                  onClick={() => navigate(`/children/${child._id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="td-lesson-copy">
                    <h4>{childName}</h4>
                    <p>{className ? `Class ${className}` : "No class assigned"}</p>
                    <div className="td-lesson-meta">
                      <span>
                        <i>
                          <GraduationCap size={13} />
                        </i>
                        {child.rollNumber || "No roll no."}
                      </span>
                      {childAge(child) ? (
                        <span>
                          <i>
                            <Users size={13} />
                          </i>
                          {childAge(child)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {child?.image ? (
                    <img className="td-lesson-avatar" src={`${UPLOADS_URL}/${child.image}`} alt="" />
                  ) : (
                    <span className="td-lesson-avatar">{initials(child.firstName, child.lastName)}</span>
                  )}
                </article>
              );
            })}
          </div>
        ) : (
          <div className="td-empty">
            <Inbox size={22} />
            <span>Assign children to show them here</span>
          </div>
        )}
      </aside>

      <Modal
        title="Update Password"
        open={resetOpen}
        onCancel={() => {
          setResetOpen(false);
          form.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            danger
            onClick={() => {
              setResetOpen(false);
              form.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="generate" onClick={() => generateParentUpdatePassword(form)}>
            Generate
          </Button>,
          <Button key="save" type="primary" onClick={() => form.submit()}>
            Update Password
          </Button>,
        ]}
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleUpdatePassword}>
          <Form.Item label="New Password" name="newPassword">
            <Input.Password size="large" placeholder="Leave blank to auto-generate" />
          </Form.Item>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const next = getFieldValue("newPassword");
                  if (!next && !value) return Promise.resolve();
                  if (!value) {
                    return Promise.reject(new Error("Please confirm the new password"));
                  }
                  if (next === value) return Promise.resolve();
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password size="large" placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
